import { Link } from "react-router-dom";
import { Check, ArrowRight, Video, ClipboardList, Repeat, Star, MapPin, PhoneCall } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { LeadForm } from "../components/LeadForm";
import { SEO } from "../components/SEO";
import { SITE, IMG } from "../data/siteData";

const steps = [
  { icon: ClipboardList, title: "Tell us about your dog", desc: "Fill out the form below — we learn your dog's history, your goals, and the issues you're facing." },
  { icon: Video, title: "Meet your trainer live", desc: "One-on-one video sessions with a certified Chinook K9 trainer, coaching you hands-on in real time." },
  { icon: Repeat, title: "Practice & progress", desc: "Customized homework between sessions keeps you and your dog moving forward every single week." },
];

const greatFor = [
  "Puppy foundations and early guidance",
  "Basic manners and obedience",
  "Leash pulling and reactivity coaching",
  "Crate training and household manners",
  "Anxiety and behaviour consults",
  "Follow-up coaching after a program",
];

const faqs = [
  { q: "What do I need for a virtual session?", a: "Just a phone, tablet, or laptop with a camera and a stable internet connection. Your trainer will guide camera placement so they can coach you live." },
  { q: "Is virtual training really effective?", a: "Yes — because your dog lives and behaves at home, coaching you there is often the fastest path to results. You learn the handling skills directly, with expert eyes on every rep." },
  { q: "Where are virtual lessons available?", a: "Anywhere in Canada. Our facility is based just outside Calgary, Alberta — but virtual sessions mean distance is never a barrier." },
  { q: "How many sessions will I need?", a: "It depends on your goals. Many owners see real change within a few sessions; your trainer will recommend a plan after the first one." },
];

export default function VirtualLessons() {
  return (
    <div data-testid="virtual-lessons-page">
      <SEO
        title="Virtual Dog Training — Online Dog Trainer Across Canada | Chinook K9"
        description="Live one-on-one virtual dog training sessions for $200/hour, available anywhere in Canada. Certified Chinook K9 trainers coach you and your dog over video — based in the Calgary area, training dogs nationwide."
        path="/virtual-dog-training"
      />

      <section className="relative min-h-[80vh] flex items-end overflow-hidden" data-testid="vl-hero">
        <img src={IMG.virtualLessons} alt="Virtual dog training session with Chinook K9" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-black/45 to-black/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-20 pt-44 w-full">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.3em] text-xs uppercase mb-4 hero-text-shadow">Virtual Lessons · Anywhere in Canada</p>
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-white max-w-3xl leading-[1.05] hero-text-shadow">
              Expert dog training, wherever you live
            </h1>
            <p className="mt-5 text-white/85 max-w-2xl leading-relaxed hero-text-shadow">
              Live, one-on-one video coaching with a certified Chinook K9 trainer — the same customized plan and expert guidance we deliver at our Calgary-area facility, now available from your living room.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a href="#book" data-testid="vl-hero-book-btn" className="bg-brand text-white font-heading font-bold px-8 py-3.5 rounded-full hover:bg-red-700 transition-colors inline-flex items-center gap-2 shadow-[0_0_20px_rgba(220,38,38,0.4)]">
                Book a Session <ArrowRight size={16} />
              </a>
              <span className="inline-block bg-black/60 backdrop-blur-sm border border-white/20 text-white font-heading font-extrabold text-xl px-6 py-2.5 rounded-full" data-testid="vl-hero-price">
                $200 <span className="text-brand text-sm font-bold">/ hour session</span>
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-24" data-testid="vl-how">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-14 max-w-2xl">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">How It Works</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">Three simple steps to a better dog</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 h-full hover:border-brand/50 transition-colors" data-testid={`vl-step-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-brand/10 text-brand flex items-center justify-center mb-5"><s.icon size={22} /></div>
                  <h3 className="font-heading text-lg font-bold text-white mb-3">{s.title}</h3>
                  <p className="text-sm text-white/65 leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 text-slate-900" data-testid="vl-great-for">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">What It's Great For</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-6">Real coaching, real results — no travel required</h2>
            <p className="text-slate-600 leading-relaxed mb-4">
              Virtual lessons put a certified trainer's eyes on your dog's behaviour right where it happens: your home,
              your yard, your neighbourhood. You build the handling skills yourself, guided every step of the way.
            </p>
            <p className="text-slate-600 leading-relaxed">
              Every session includes a customized plan, homework between sessions, and support via phone or email
              during and after your training.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="border border-black/10 rounded-2xl p-8">
              <ul className="space-y-3" data-testid="vl-great-for-list">
                {greatFor.map((g) => (
                  <li key={g} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
                    <Check size={16} className="text-brand shrink-0 mt-0.5" /> {g}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-24 border-t border-white/10" data-testid="vl-why">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-12 text-center max-w-2xl mx-auto">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Why Chinook K9</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">Calgary-area experts, coaching nationwide</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { icon: Star, big: "4.9", small: "Google rating — 120+ reviews" },
              { icon: MapPin, big: "Calgary, AB", small: "Based in Rocky View County, training across Canada" },
              { icon: Video, big: "100% Live", small: "One-on-one sessions, never pre-recorded" },
            ].map((s, i) => (
              <Reveal key={s.big} delay={i * 0.1}>
                <div className="text-center" data-testid={`vl-proof-${i}`}>
                  <s.icon size={22} className="text-brand mx-auto mb-4" />
                  <p className="font-heading text-3xl font-extrabold text-white mb-2">{s.big}</p>
                  <p className="text-sm text-white/60 leading-relaxed">{s.small}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 text-slate-900 border-t border-black/10" data-testid="vl-faq">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal className="mb-12 text-center">
            <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Common Questions</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Virtual training FAQs</h2>
          </Reveal>
          <div className="space-y-5">
            {faqs.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.06}>
                <div className="border border-black/10 rounded-2xl p-7" data-testid={`vl-faq-${i}`}>
                  <h3 className="font-heading text-base font-bold mb-2">{f.q}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.a}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-24 text-slate-900 border-t border-black/10" id="book" data-testid="vl-book">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-10">
              <p className="text-brand font-heading font-bold tracking-[0.25em] text-xs uppercase mb-4">Get Started</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">Book your first virtual session</h2>
              <p className="text-slate-600">Tell us about your dog — we'll set up your first one-hour video session ($200).</p>
            </div>
            <div className="bg-obsidian rounded-2xl p-6 sm:p-10">
              <LeadForm compact defaultPrograms={["Virtual Lessons"]} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-obsidian py-20 border-t border-white/10" data-testid="vl-cta">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-4">Prefer in-person training?</h2>
            <p className="text-white/60 mb-8 max-w-xl mx-auto">If you're near Calgary, explore our board & train and private lesson programs at the facility.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/services/private-training" data-testid="vl-private-cta" className="bg-brand text-white font-heading font-bold px-8 py-3.5 rounded-full hover:bg-red-700 transition-colors inline-flex items-center justify-center gap-2">
                Private Training <ArrowRight size={16} />
              </Link>
              <a href={SITE.phoneHref} data-testid="vl-call-cta" className="border border-white/25 text-white font-heading font-semibold px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2">
                <PhoneCall size={16} /> {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
